import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutGrid,
  History,
  Users,
  Settings,
  Plus,
  UserPlus,
  MessageCircle,
  ContactRound,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useApp } from '../context';
import { supportApi } from '../lib/api';

export default function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = useApp();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [unreadTickets, setUnreadTickets] = useState(0);

  // Re-fetch unread count on every navigation (e.g. returning from ticket detail)
  useEffect(() => {
    if (state.isGuestMode || !state.user) return;
    supportApi
      .unreadCount()
      .then((r) => setUnreadTickets(r.count))
      .catch(() => {});
  }, [state.isGuestMode, state.user, location.pathname]);

  const leftNav = [
    { icon: LayoutGrid, label: 'Dashboard', path: '/' },
    { icon: History, label: 'Activity', path: '/activity' },
  ];
  const rightNav = [
    { icon: Users, label: 'Contacts', path: '/contacts' },
    { icon: Settings, label: 'Settings', path: '/profile' },
  ];

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleMenuAction = (path: string) => {
    setIsMenuOpen(false);
    navigate(path);
  };

  const hideNav =
    [
      '/notifications',
      '/log',
      '/add-contact',
      '/import-contacts',
      '/privacy-security',
      '/help-support',
    ].includes(location.pathname) ||
    location.pathname.startsWith('/edit-contact') ||
    location.pathname.startsWith('/support/') ||
    location.pathname.startsWith('/personal-info/edit/');

  return (
    <div className="min-h-screen bg-[#f8f5f5] text-slate-900 font-sans flex flex-col max-w-md mx-auto shadow-2xl relative overflow-hidden">
      {/* Backdrop when menu is open */}
      {!hideNav && (
        <div
          className={cn(
            'fixed inset-0 z-40 transition-all duration-300',
            isMenuOpen
              ? 'bg-slate-900/40 backdrop-blur-[2px] pointer-events-auto'
              : 'bg-transparent pointer-events-none',
          )}
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      <main
        className={cn(
          'flex-1 overflow-y-auto transition-all duration-300',
          !hideNav && 'pb-28',
          isMenuOpen && 'blur-[2px]',
        )}
      >
        {children}
      </main>

      {/* Bottom Navigation */}
      {!hideNav && (
        <nav className="fixed bottom-4 left-4 right-4 bg-white rounded-3xl h-[72px] flex items-center z-50 max-w-md mx-auto overflow-visible px-2 shadow-[0_2px_20px_rgba(0,0,0,0.08)]">
          <div className="flex w-full justify-between items-center relative h-full">
            {/* Left Tabs */}
            <div
              className={cn(
                'flex flex-1 justify-evenly items-center transition-opacity duration-300',
                isMenuOpen && 'opacity-30 pointer-events-none',
              )}
            >
              {leftNav.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      'flex flex-col items-center justify-center gap-1 transition-colors w-16',
                      isActive ? 'text-[#fa8585]' : 'text-slate-400',
                    )}
                  >
                    <item.icon size={22} strokeWidth={isActive ? 2.5 : 2} />
                    <span className="text-[9px] font-bold uppercase tracking-widest">
                      {item.label}
                    </span>
                  </Link>
                );
              })}
            </div>

            {/* Central FAB Area */}
            <div className="w-16 h-full flex items-center justify-center relative shrink-0">
              <div className="absolute bottom-6 flex flex-col items-center z-50">
                {/* Card container (menu items only when open) */}
                <div
                  className={cn(
                    'transition-all duration-300 ease-out flex flex-col items-center overflow-hidden origin-bottom',
                    isMenuOpen
                      ? 'w-56 bg-white rounded-[32px] shadow-2xl shadow-slate-900/15 px-5 pt-5 pb-8'
                      : 'w-14 h-0 rounded-[32px] opacity-0',
                  )}
                >
                  <div
                    className={cn(
                      'w-full flex flex-col gap-1 transition-all duration-200',
                      isMenuOpen
                        ? 'opacity-100 scale-100'
                        : 'opacity-0 scale-90 pointer-events-none',
                    )}
                  >
                    <button
                      onClick={() => handleMenuAction('/log')}
                      className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-[#fa8585]/5 active:bg-[#fa8585]/10 transition-colors"
                    >
                      <div className="size-10 rounded-full bg-[#fa8585]/10 flex items-center justify-center flex-shrink-0">
                        <MessageCircle size={18} className="text-[#fa8585]" />
                      </div>
                      <div className="text-left whitespace-nowrap">
                        <p className="text-sm font-semibold text-slate-800">Log Interaction</p>
                        <p className="text-[11px] text-slate-400">Record a touchpoint</p>
                      </div>
                    </button>

                    <div className="w-full h-px bg-slate-100" />

                    <button
                      onClick={() => handleMenuAction('/add-contact')}
                      className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-[#fa8585]/5 active:bg-[#fa8585]/10 transition-colors"
                    >
                      <div className="size-10 rounded-full bg-[#fa8585]/10 flex items-center justify-center flex-shrink-0">
                        <UserPlus size={18} className="text-[#fa8585]" />
                      </div>
                      <div className="text-left whitespace-nowrap">
                        <p className="text-sm font-semibold text-slate-800">Add Contact</p>
                        <p className="text-[11px] text-slate-400">Add someone new</p>
                      </div>
                    </button>

                    <div className="w-full h-px bg-slate-100" />

                    <button
                      onClick={() => handleMenuAction('/import-contacts')}
                      className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-[#fa8585]/5 active:bg-[#fa8585]/10 transition-colors"
                    >
                      <div className="size-10 rounded-full bg-[#fa8585]/10 flex items-center justify-center flex-shrink-0">
                        <ContactRound size={18} className="text-[#fa8585]" />
                      </div>
                      <div className="text-left whitespace-nowrap">
                        <p className="text-sm font-semibold text-slate-800">Import Contacts</p>
                        <p className="text-[11px] text-slate-400">From phone or file</p>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Toggle button — straddles the card bottom edge when open */}
                <button
                  onClick={toggleMenu}
                  className={cn(
                    'flex items-center justify-center transition-all duration-300 flex-shrink-0 relative z-10',
                    isMenuOpen
                      ? 'size-12 rounded-full bg-[#fa8585] text-white shadow-lg -mt-6 ring-[5px] ring-white'
                      : 'size-14 rounded-full bg-[#fa8585] text-white shadow-xl shadow-[#fa8585]/40 active:scale-95',
                  )}
                >
                  <Plus
                    size={isMenuOpen ? 22 : 32}
                    className={cn('transition-transform duration-300', isMenuOpen && 'rotate-45')}
                  />
                </button>
              </div>
            </div>

            {/* Right Tabs */}
            <div
              className={cn(
                'flex flex-1 justify-evenly items-center transition-opacity duration-300',
                isMenuOpen && 'opacity-30 pointer-events-none',
              )}
            >
              {rightNav.map((item) => {
                const isActive = location.pathname === item.path;
                const showBadge = item.path === '/profile' && unreadTickets > 0;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      'flex flex-col items-center justify-center gap-1 transition-colors w-16 relative',
                      isActive ? 'text-[#fa8585]' : 'text-slate-400',
                    )}
                  >
                    <item.icon size={22} strokeWidth={isActive ? 2.5 : 2} />
                    {showBadge && (
                      <span className="absolute top-0 right-2.5 size-2 bg-red-500 rounded-full" />
                    )}
                    <span className="text-[9px] font-bold uppercase tracking-widest">
                      {item.label}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </nav>
      )}
    </div>
  );
}
